import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useAuth } from "../Context/auth";
import MenuLateral from '../Components/MenuPrincipal/MenuLateral';
import MarcasGrid from '../Components/MarcasGrid/marcasGrid';
import Pagination from '../Components/Pagination/Pagination';
import './Marcas.css';
// Recompiling to clear cache

function Marcas() {
    const { currentUser, appUserData } = useAuth();
    const [marcas, setMarcas] = useState([]); // Filtered list
    const [allMarcas, setAllMarcas] = useState([]); // Full list
    const [loading, setLoading] = useState(true);
    const [pesquisa, setPesquisa] = useState('');
    const [textoPesquisa, setTextoPesquisa] = useState('');
    const [trigger, setTrigger] = useState(0);

    // Paginação
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);

    // Verifica permissão de leitura
    useEffect(() => {
        if (appUserData && !appUserData['ler_marcas']) {
            Swal.fire({
                icon: 'error',
                title: 'Acesso Negado',
                text: 'Você não tem permissão para visualizar esta tela.',
                confirmButtonColor: '#2185c6'
            }).then(() => {
                window.location.href = '/app/home';
            });
        }
    }, [appUserData]);

    // Fetch ALL Marcas
    useEffect(() => {
        const fetchMarcas = async () => {
            if (!currentUser) return;
            setLoading(true);
            try {
                const token = await currentUser.getIdToken();
                const API_URL = process.env.REACT_APP_API_URL;
                const url = new URL(`${API_URL}/api/marcas`);

                // Client-side filtering: Fetch ALL, then filter locally
                // url.searchParams.append('search', textoPesquisa); // REMOVED

                const response = await fetch(url.toString(), {
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                if (!response.ok) throw new Error("Falha ao carregar marcas.");
                const data = await response.json();
                setAllMarcas(data);
                setMarcas(data); // Initialize filtered list with full list
            } catch (error) {
                console.error("Erro ao buscar marcas: ", error);
                Swal.fire("Erro", "Não foi possível carregar as marcas.", "error");
            } finally {
                setLoading(false);
            }
        };

        fetchMarcas();
    }, [currentUser, trigger]);

    // Filter Logic
    useEffect(() => {
        if (!textoPesquisa) {
            setMarcas(allMarcas);
        } else {
            const lowerSearch = textoPesquisa.toLowerCase();
            const filtered = allMarcas.filter(marca =>
                (marca.descricao && marca.descricao.toLowerCase().includes(lowerSearch))
            );
            setMarcas(filtered);
        }
        setCurrentPage(1);
    }, [textoPesquisa, allMarcas]);

    // Debounce Search Input
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setTextoPesquisa(pesquisa);
        }, 500); // 500ms delay for "quick filter" feel
        return () => clearTimeout(timeoutId);
    }, [pesquisa]);

    const handlePesquisar = () => {
        setTextoPesquisa(pesquisa);
    };

    const handleAtualizar = () => {
        setPesquisa('');
        setTextoPesquisa('');
    };

    async function deleteMarca(id) {
        // Validation for Delete (Requirement 4)
        if (appUserData && appUserData['excluir_marcas'] === false) {
            Swal.fire({
                icon: 'error',
                title: 'Acesso Negado',
                text: 'Este usuário não possui permissão de exclusão. Por favor, contate o administrador.',
                confirmButtonColor: '#2185c6'
            });
            return;
        }

        Swal.fire({
            title: "Você tem certeza?",
            text: "Esta ação não poderá ser revertida!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#2185c6",
            cancelButtonColor: "#DC3545",
            confirmButtonText: "Sim, excluir!",
            cancelButtonText: "Cancelar"
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const token = await currentUser.getIdToken();
                    const API_URL = process.env.REACT_APP_API_URL;
                    const response = await fetch(`${API_URL}/api/marcas/${id}`, {
                        method: 'DELETE',
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (!response.ok) {
                        const errorData = await response.json();
                        throw new Error(errorData.error || "Erro ao deletar");
                    }
                    setTrigger(Date.now());
                    Swal.fire("Excluído!", "A marca foi deletada com sucesso.", "success");
                } catch (error) {
                    Swal.fire("Erro!", error.message, "error");
                }
            }
        });
    }

    // Paginação Logic
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentMarcas = marcas.slice(indexOfFirstItem, indexOfLastItem);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);
    const changeItemsPerPage = (limit) => {
        setItemsPerPage(limit);
        setCurrentPage(1);
    };

    if (loading) {
        return (
            <div className="marcas-page-container" style={{ display: 'flex' }}>
                <MenuLateral />
                <div className="d-flex justify-content-center align-items-center w-100" style={{ height: '100vh', marginTop: '60px' }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Carregando...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="marcas-page-container" style={{ display: 'flex', paddingTop: 0 }}>
            <MenuLateral />

            <div className="content-with-sidebar">
                <div className="marcas-header">
                    <h1 className="marcas-title">
                        <i className="fas fa-tag text-primary"></i>
                        Listagem de <span className="highlight">Marcas</span>
                    </h1>
                </div>

                <main className="marcas-content">
                    <div className="marcas-controls">
                        <Link
                            to="/app/novamarca"
                            className="btn-primary-custom me-2"
                            onClick={(e) => {
                                // Validation for Create (Requirement 2)
                                if (appUserData && appUserData['cadastrar_marcas'] === false) {
                                    e.preventDefault();
                                    Swal.fire({
                                        icon: 'error',
                                        title: 'Acesso Negado',
                                        text: 'Este usuário não possui permissão de cadastro. Por favor, contate o administrador.',
                                        confirmButtonColor: '#2185c6'
                                    });
                                }
                            }}
                        >
                            <i className="fas fa-plus"></i> Nova Marca
                        </Link>
                        <div className="search-wrapper">
                            <i className="fas fa-search search-icon"></i>
                            <input
                                type="text"
                                className="search-input-custom"
                                placeholder="Pesquisar por descrição..."
                                value={pesquisa}
                                onChange={(e) => setPesquisa(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handlePesquisar()}
                            />
                        </div>
                        <button className="btn-icon-custom" onClick={handleAtualizar} disabled={loading} title="Atualizar Lista">
                            <i className="fas fa-sync-alt"></i>
                        </button>
                    </div>

                    <div className="marcas-grid-container">
                        <MarcasGrid arrayMarcas={currentMarcas} clickDelete={deleteMarca} userPermissions={appUserData} />
                    </div>
                </main>
            </div>

            <Pagination
                totalItems={marcas.length}
                itemsPerPage={itemsPerPage}
                currentPage={currentPage}
                onPageChange={paginate}
                onItemsPerPageChange={changeItemsPerPage}
                loading={loading}
            />
        </div>
    );
}

export default Marcas;